import { JSX, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import MovieCardSkeleton from "@/components/cards/MovieCard/MovieCard.skeleton.tsx";
import FilterSection from "@/components/catalog/FilterSection.tsx";
import GroupTransition from "@/components/helpers/GroupTransition/GroupTransition.tsx";
import MoviesGrid from "@/components/helpers/MoviesGrid.tsx";
import { IconSadFace } from "@/components/icons";

import { useGetContent } from "@/hooks/api/content.hooks.ts";

const CatalogPage = (): JSX.Element => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { id } = useParams() as { id: string };
  const { ref: inViewRef, inView } = useInView({ threshold: 0.2 });
  const [searchParams] = useSearchParams();

  const [search] = useState<string>("");
  const { data, fetchNextPage, hasNextPage, isFetching } = useGetContent({
    limit: 48,
    category: parseInt(id),
    search,
    content_lang: searchParams.get("language"),
    country: searchParams.get("country"),
    genres: searchParams.get("genre"),
    year: searchParams.get("year"),
    ordering: searchParams.get("ordering"),
    distributor: searchParams.get("distributor"),
    is_free: searchParams.get("is_free") == "true",
  });

  const key = pathname.split("/")[2];
  const flattenedData = data?.pages.flatMap((page) => page.results) || [];

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetching]);

  return (
    <GroupTransition<HTMLDivElement> transitionKey={key}>
      {({ ref }) => (
        <div ref={ref}>
          <FilterSection />
          <section className={"rounded-7.5 max-lg:py-0"}>
            {flattenedData && flattenedData.length ? (
              <MoviesGrid content={flattenedData} />
            ) : !isFetching ? (
              <div className={"flex h-[50vh] flex-1 flex-col items-center justify-center"}>
                <IconSadFace className={"size-20 fill-grey-1"} />
                <div className={"mt-4 max-w-[27.5rem] text-center"}>
                  <h1 className={"text-2xl font-semibold"}>{t("error.nothing-found")}</h1>
                  <p className={"mt-2 text-lg/6 text-on-surface"}>{t("error.nothing-found-message")}</p>
                </div>
              </div>
            ) : null}
            {isFetching ? (
              <div className={"movies-grid mt-[16px]"}>
                {[...Array(12).keys()].map((_, index) => (
                  <MovieCardSkeleton key={index} />
                ))}
              </div>
            ) : null}
            {flattenedData.length ? <div ref={inViewRef} className={"h-px w-full"} /> : null}
          </section>
        </div>
      )}
    </GroupTransition>
  );
};

export default CatalogPage;
